/**
 * Transaction process graph for product orders:
 *   - default-purchase
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // Customer starts checkout and requests payment intent creation.
  REQUEST_PAYMENT: 'transition/request-payment',

  // Customer can also start from inquiry and continue to payment request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Customer confirms payment intent (authorization), but capture is delayed
  // until provider accepts the order.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If payment is not confirmed in time.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Provider decision phase for pending acceptance orders.
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',
  AUTO_DECLINE: 'transition/auto-decline',

  // Provider or operator can mark product shipped/delivered after acceptance.
  MARK_DELIVERED: 'transition/mark-delivered',
  OPERATOR_MARK_DELIVERED: 'transition/operator-mark-delivered',

  // Legacy transition kept for backwards compatibility with old purchase flow.
  // New flow should use MARK_RECEIVED after MARK_DELIVERED.
  MARK_RECEIVED_FROM_PURCHASED: 'transition/mark-received-from-purchased',

  // Automatic cancellation if nothing is delivered.
  AUTO_CANCEL: 'transition/auto-cancel',

  // Operator can cancel purchase before delivery / receipt.
  CANCEL: 'transition/cancel',

  // Customer marks received after delivered.
  MARK_RECEIVED: 'transition/mark-received',

  // System auto marks received if customer does not react.
  AUTO_MARK_RECEIVED: 'transition/auto-mark-received',

  // Disputes.
  DISPUTE: 'transition/dispute',
  OPERATOR_DISPUTE: 'transition/operator-dispute',

  // Disputed order cancellation / payout paths.
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',
  MARK_RECEIVED_FROM_DISPUTED: 'transition/mark-received-from-disputed',

  // Revision rounds after customer marks order as received.
  REQUEST_REVISION_1: 'transition/request-revision-1',
  SUBMIT_REVISION_1: 'transition/submit-revision-1',
  REQUEST_REVISION_2: 'transition/request-revision-2',
  SUBMIT_REVISION_2: 'transition/submit-revision-2',

  // Completion paths.
  AUTO_COMPLETE: 'transition/auto-complete',
  AUTO_COMPLETE_AFTER_REVISION_1: 'transition/auto-complete-after-revision-1',
  AUTO_COMPLETE_AFTER_REVISION_2: 'transition/auto-complete-after-revision-2',

  // Reviews.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  PURCHASED: 'purchased',
  DELIVERED: 'delivered',
  RECEIVED: 'received',
  REVISION_REQUESTED_1: 'revision-requested-1',
  REVISION_DELIVERED_1: 'revision-delivered-1',
  REVISION_REQUESTED_2: 'revision-requested-2',
  REVISION_DELIVERED_2: 'revision-delivered-2',
  DISPUTED: 'disputed',
  CANCELED: 'canceled',
  COMPLETED: 'completed',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  id: 'default-purchase/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },

    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},

    [states.PREAUTHORIZED]: {
      on: {
        [transitions.ACCEPT]: states.PURCHASED,
        [transitions.DECLINE]: states.DECLINED,
        [transitions.AUTO_DECLINE]: states.DECLINED,
      },
    },

    [states.DECLINED]: { type: 'final' },

    [states.PURCHASED]: {
      on: {
        [transitions.MARK_DELIVERED]: states.DELIVERED,
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        // Legacy path from older process releases.
        [transitions.MARK_RECEIVED_FROM_PURCHASED]: states.RECEIVED,
        [transitions.AUTO_CANCEL]: states.CANCELED,
        [transitions.CANCEL]: states.CANCELED,
      },
    },

    [states.CANCELED]: { type: 'final' },

    [states.DELIVERED]: {
      on: {
        [transitions.MARK_RECEIVED]: states.RECEIVED,
        [transitions.AUTO_MARK_RECEIVED]: states.RECEIVED,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,
      },
    },

    [states.RECEIVED]: {
      on: {
        [transitions.REQUEST_REVISION_1]: states.REVISION_REQUESTED_1,
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
      },
    },

    [states.REVISION_REQUESTED_1]: {
      on: {
        [transitions.SUBMIT_REVISION_1]: states.REVISION_DELIVERED_1,
      },
    },

    [states.REVISION_DELIVERED_1]: {
      on: {
        [transitions.REQUEST_REVISION_2]: states.REVISION_REQUESTED_2,
        [transitions.AUTO_COMPLETE_AFTER_REVISION_1]: states.COMPLETED,
      },
    },

    [states.REVISION_REQUESTED_2]: {
      on: {
        [transitions.SUBMIT_REVISION_2]: states.REVISION_DELIVERED_2,
      },
    },

    [states.REVISION_DELIVERED_2]: {
      on: {
        [transitions.AUTO_COMPLETE_AFTER_REVISION_2]: states.COMPLETED,
      },
    },

    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },

    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },

    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
export const isRelevantPastTransition = transition => {
  return [
    transitions.CONFIRM_PAYMENT,
    transitions.ACCEPT,
    transitions.DECLINE,
    transitions.AUTO_DECLINE,
    transitions.AUTO_CANCEL,
    transitions.CANCEL,
    transitions.MARK_DELIVERED,
    transitions.OPERATOR_MARK_DELIVERED,
    transitions.MARK_RECEIVED,
    transitions.MARK_RECEIVED_FROM_PURCHASED,
    transitions.AUTO_MARK_RECEIVED,
    transitions.MARK_RECEIVED_FROM_DISPUTED,
    transitions.DISPUTE,
    transitions.OPERATOR_DISPUTE,
    transitions.AUTO_COMPLETE,
    transitions.AUTO_COMPLETE_AFTER_REVISION_1,
    transitions.AUTO_COMPLETE_AFTER_REVISION_2,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.REQUEST_REVISION_1,
    transitions.SUBMIT_REVISION_1,
    transitions.REQUEST_REVISION_2,
    transitions.SUBMIT_REVISION_2,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
export const isPrivileged = transition => {
  return [transitions.REQUEST_PAYMENT, transitions.REQUEST_PAYMENT_AFTER_INQUIRY].includes(
    transition
  );
};

// Check when transaction is completed (item is received and review notifications sent)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.AUTO_COMPLETE,
    transitions.AUTO_COMPLETE_AFTER_REVISION_1,
    transitions.AUTO_COMPLETE_AFTER_REVISION_2,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.DECLINE,
    transitions.AUTO_DECLINE,
    transitions.CANCEL,
    transitions.AUTO_CANCEL,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [
  states.PREAUTHORIZED,
  states.PURCHASED,
  states.REVISION_REQUESTED_1,
  states.REVISION_REQUESTED_2,
];
